import {
  getCaseData,
  contextToStorePath,
  defaultArray
} from 'app/infrastructure/ui/renderer/resolvers';
import { getEntityTypeFromPath } from 'app/infrastructure/store/entity';
import { ComplexValue } from 'app/infrastructure/ui/form/types';
import { IteratorType } from 'app/infrastructure/ui';
import { ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import { entityFilter } from 'app/area/case/entity-filters';
import { Entity } from 'app/types-shared';
import sortBy from 'lodash/sortby';
import { getChildEntities } from 'app/infrastructure/store/query';
import { ChildItem, IteratorContext } from 'app/infrastructure/ui/renderer/iterator-types';

const sortEntities = (
  props: ConnectedComponentProps,
  entities: Entity[]
): Entity[] => {
  // Using lodash sortBy for stable sort to preserve ordering beyond the value provided.
  if (props.sortValue) {
    return sortBy(entities, props.sortValue);
  }

  return entities;
};

const getDataRowsIteratorChildItems = (
  state: any,
  props: ConnectedComponentProps
): ChildItem[] => {
  const descriptor = props.componentDescriptor;
  const context = props.resolveContext;
  const entities = getCaseData(state, props);
  const childEntityType = getEntityTypeFromPath(descriptor.entityTypePath);

  const childEntities = sortEntities(
    props,
    getChildEntities(
      entities,
      contextToStorePath(descriptor, context),
      childEntityType
    )
  );

  return (
    (childEntities
      .filter(e => entityFilter(e, props))
      .map(i => ({
        storePath: i.id,
        primaryKey: i.primaryKey
      })) as ChildItem[]) || defaultArray
  );
};

const getCodetableIteratorChildItems = (
  state: any,
  props: ConnectedComponentProps
): ChildItem[] => {
  const { componentDescriptor: descriptor, options } = props;
  const context = props.resolveContext;
  const childEntityType = getEntityTypeFromPath(descriptor.entityTypePath);

  const dataRowItems = getDataRowsIteratorChildItems(state, props);

  const rowIsVirtual = (option: ComplexValue<number>) => {
    return !dataRowItems.find(
      d => d.primaryKey && d.primaryKey.value === option.value
    );
  };

  return (
    (options &&
      options.map(c => {
        // If this iterator does not have a type, we cannot infer the storePath so we default to the current context.
        const storePath = [...context.path];

        if (childEntityType) {
          storePath.push({ [childEntityType]: c.value });
        }

        return {
          storePath,
          primaryKey: c,
          ...(rowIsVirtual(c) ? { virtual: true } : undefined)
        };
      })) ||
    defaultArray
  );
};

export const getIteratorChildItems = (
  type: IteratorType,
  state: any,
  props: ConnectedComponentProps
): ChildItem[] => {
  switch (type) {
    case IteratorType.DataRows:
      return getDataRowsIteratorChildItems(state, props);
    case IteratorType.CodeTable:
      return getCodetableIteratorChildItems(state, props);
    default:
      return defaultArray;
  }
};

/**
 * Props required by a iterator component to be able to enumerate child data in the correct context
 *
 * @param state component application state
 * @param configProps component props of the currently connected component
 * @param currentUiProps iterator behavior sometimes depend on props that are resolved specifically for the component,
 * for instance when selected from data.
 */
export const getIteratorContext = (
  state: any,
  configProps: any,
  currentUiProps: ConnectedComponentProps
): IteratorContext => {
  const descriptor = configProps.componentDescriptor;
  const connectingAnIterator = !!descriptor.iterate;
  if (connectingAnIterator) {
    const childItems: ChildItem[] = getIteratorChildItems(
      descriptor.iterate.type,
      state,
      currentUiProps
    );

    return {
      // actions are used for binding action creators in
      // callbacks
      actions: configProps.actions,
      childItems,
      statePath: configProps.statePath
    };
  }

  return {};
};
